<script setup lang="ts">
    import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
    import type { AnonymizerUsageResult, GdprWorkspacePagePayload, WorkspaceStepKey } from './types';
    import { copyToClipboard, fetchUsage, getWorkspaceErrorMessage, getWorkspaceErrorUsage, login, logout, refreshLogin, requestAnonymization, requestPersonalization, setAccessToken, setSessionExpiredHandler } from './workspace';
    import PrototypeIcon from './PrototypeIcon.vue';

    const props = defineProps<{
        payload: GdprWorkspacePagePayload;
    }>();

    const originalText = ref('');
    const anonymizedText = ref('');
    const aiResponse = ref('');
    const sessionId = ref('');
    const personalizedText = ref('');
    const showSecondPhase = ref(false);
    const usage = ref<AnonymizerUsageResult>({
        limit: props.payload.usageLimit,
        used: props.payload.creditsUsedOnLoad,
        remaining: Math.max(props.payload.usageLimit - props.payload.creditsUsedOnLoad, 0),
        isExhausted: props.payload.creditsUsedOnLoad >= props.payload.usageLimit,
        isUnlimited: false,
    });
    const accountEmail = ref<string | null>(null);
    const authReady = ref(false);
    const showLoginDialog = ref(false);
    const showProfileMenu = ref(false);
    const loginEmail = ref('');
    const loginPassword = ref('');
    const loginError = ref('');
    const isLoggingIn = ref(false);
    const loginDialog = ref<HTMLElement | null>(null);
    const requestError = ref('');
    const personalizationError = ref('');
    const isSubmitting = ref(false);
    const isPersonalizing = ref(false);
    const showTrialCompleteModal = ref(false);
    const hasShownTrialCompleteModal = ref(false);
    const trialCompleteModal = ref<HTMLElement | null>(null);
    const copiedButton = ref<string | null>(null);
    let copyFeedbackTimer: number | undefined;

    const usageLimit = computed(() => usage.value.limit ?? props.payload.usageLimit);
    const creditsRemaining = computed(() => usage.value.remaining ?? 0);
    const outOfCredits = computed(() => !accountEmail.value && usage.value.isExhausted);
    const lastCredit = computed(() => creditsRemaining.value === 1);
    const usageRemainingText = computed(() => props.payload.usageRemainingLabel
        .replace('{remaining}', String(creditsRemaining.value))
        .replace('{limit}', String(usageLimit.value)));

    const currentStep = computed<WorkspaceStepKey>(() => {
        if (personalizedText.value) {
            return 'personalized';
        }

        if (aiResponse.value && showSecondPhase.value) {
            return 'ai';
        }

        if (anonymizedText.value) {
            return 'anonymized';
        }

        return 'original';
    });

    function scrollTo(id: string): void {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function openTrialCompleteModal(): void {
        showTrialCompleteModal.value = true;
        void nextTick(() => trialCompleteModal.value?.focus());
    }

    function closeTrialCompleteModal(): void {
        showTrialCompleteModal.value = false;
    }

    function handleModalKeydown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            closeTrialCompleteModal();
            showLoginDialog.value = false;
            showProfileMenu.value = false;
        }
    }

    function openAccount(): void {
        if (accountEmail.value) {
            showProfileMenu.value = !showProfileMenu.value;
            return;
        }
        showLoginDialog.value = true;
        void nextTick(() => loginDialog.value?.focus());
    }

    async function handleLogin(): Promise<void> {
        if (isLoggingIn.value) return;
        isLoggingIn.value = true;
        loginError.value = '';
        try {
            const result = await login(loginEmail.value.trim(), loginPassword.value);
            setAccessToken(result.token);
            accountEmail.value = result.email;
            showLoginDialog.value = false;
            showTrialCompleteModal.value = false;
            loginPassword.value = '';
            try { usage.value = await fetchUsage(props.payload.usageEndpointUrl); }
            catch (error) { requestError.value = getWorkspaceErrorMessage(error, props.payload.usageLoadErrorMessage); }
        } catch (error) {
            loginError.value = getWorkspaceErrorMessage(error, 'Inloggen is niet gelukt. Controleer je e-mailadres en wachtwoord.');
        } finally {
            isLoggingIn.value = false;
        }
    }

    async function handleLogout(): Promise<void> {
        try {
            await logout();
        } catch (error) {
            requestError.value = getWorkspaceErrorMessage(error, 'Uitloggen is niet gelukt.');
            return;
        }
        setAccessToken(null);
        accountEmail.value = null;
        showProfileMenu.value = false;
        try {
            usage.value = await fetchUsage(props.payload.usageEndpointUrl);
            if (usage.value.isExhausted) openTrialCompleteModal();
        } catch (error) {
            requestError.value = getWorkspaceErrorMessage(error, props.payload.usageLoadErrorMessage);
        }
    }

    onMounted(async () => {
        window.addEventListener('keydown', handleModalKeydown);
        setSessionExpiredHandler(() => {
            accountEmail.value = null;
            showProfileMenu.value = false;
            void fetchUsage(props.payload.usageEndpointUrl)
                .then((value) => { usage.value = value; })
                .catch((error) => { requestError.value = getWorkspaceErrorMessage(error, props.payload.usageLoadErrorMessage); });
        });

        try {
            const restored = await refreshLogin().catch(() => null);
            if (restored) {
                setAccessToken(restored.token);
                accountEmail.value = restored.email;
            }
            usage.value = await fetchUsage(props.payload.usageEndpointUrl);
            if (!accountEmail.value && usage.value.isExhausted) {
                openTrialCompleteModal();
            }
        } catch (error) {
            requestError.value = getWorkspaceErrorMessage(error, props.payload.usageLoadErrorMessage);
        } finally {
            authReady.value = true;
        }
    });

    onBeforeUnmount(() => {
        window.removeEventListener('keydown', handleModalKeydown);
        setSessionExpiredHandler(null);
        window.clearTimeout(copyFeedbackTimer);
    });

    function clearRequestError(): void {
        requestError.value = '';
    }

    function clearPersonalizationError(): void {
        personalizationError.value = '';
    }

    function clearAllRequestErrors(): void {
        clearRequestError();
        clearPersonalizationError();
    }

    async function handleAnonymize(): Promise<void> {
        if (!originalText.value.trim() || isSubmitting.value) {
            return;
        }

        if (outOfCredits.value) {
            openTrialCompleteModal();
            return;
        }

        clearAllRequestErrors();
        isSubmitting.value = true;

        try {
            const response = await requestAnonymization(props.payload.anonymizeEndpointUrl, {
                text: originalText.value,
            });

            usage.value = response.usage;

            anonymizedText.value = response.anonymizedText ?? '';
            sessionId.value = response.sessionId ?? '';
            aiResponse.value = '';
            personalizedText.value = '';
            showSecondPhase.value = true;
        } catch (error) {
            const errorUsage = getWorkspaceErrorUsage(error);

            if (errorUsage) {
                usage.value = errorUsage;
            }

            requestError.value = outOfCredits.value
                ? props.payload.exhaustedText
                : getWorkspaceErrorMessage(error, props.payload.anonymizeErrorMessage);
        } finally {
            isSubmitting.value = false;
        }
    }

    async function handlePersonalize(): Promise<void> {
        if (!aiResponse.value.trim() || isPersonalizing.value) {
            return;
        }

        clearPersonalizationError();
        isPersonalizing.value = true;

        try {
            const response = await requestPersonalization(props.payload.personalizeEndpointUrl, {
                text: aiResponse.value,
                session: sessionId.value,
            });

            personalizedText.value = response.personalizedText ?? '';
        } catch (error) {
            personalizationError.value = getWorkspaceErrorMessage(error, props.payload.personalizeErrorMessage);
        } finally {
            isPersonalizing.value = false;
        }
    }

    async function handleCopy(button: string, value: string): Promise<void> {
        await copyToClipboard(value);
        copiedButton.value = button;
        window.clearTimeout(copyFeedbackTimer);
        copyFeedbackTimer = window.setTimeout(() => {
            copiedButton.value = null;
        }, 1800);
    }

    async function handleCopyPersonalized(button: string): Promise<void> {
        await handleCopy(button, personalizedText.value);

        if (outOfCredits.value && !hasShownTrialCompleteModal.value) {
            hasShownTrialCompleteModal.value = true;
            openTrialCompleteModal();
        }
    }

    function handleReset(): void {
        if (outOfCredits.value) {
            openTrialCompleteModal();
            return;
        }

        clearAllRequestErrors();
        originalText.value = '';
        anonymizedText.value = '';
        aiResponse.value = '';
        personalizedText.value = '';
        sessionId.value = '';
        showSecondPhase.value = false;
    }
</script>

<template>
    <div class="gdpr-prototype gdpr-workspace" data-cy="workspace-page">
        <header class="gdpr-topbar">
            <div class="gdpr-shell gdpr-topbar__inner">
                <div class="gdpr-brand">
                    <div class="gdpr-brand__mark">
                        <PrototypeIcon name="shield" />
                    </div>
                    <span>{{ payload.siteName }}</span>
                </div>

                <div class="gdpr-topbar__actions">
                    <div v-if="authReady && !accountEmail" class="gdpr-trial-meter">
                        <div class="gdpr-trial-meter__copy">
                            <small>{{ payload.usageTitleLabel }}</small>
                            <span :class="{ 'is-low': creditsRemaining <= 1 }">
                                {{ usageRemainingText }}
                            </span>
                        </div>
                        <div class="gdpr-trial-meter__bar">
                            <div :style="{ width: `${Math.max(0, Math.min(100, usageLimit > 0 ? (creditsRemaining / usageLimit) * 100 : 0))}%` }"></div>
                        </div>
                    </div>

                    <button class="gdpr-button gdpr-button--secondary gdpr-button--small" data-cy="new-session-button" type="button" @click="handleReset">
                        <PrototypeIcon name="plus" />
                        <span>{{ payload.newSessionLabel }}</span>
                    </button>

                    <div class="gdpr-profile-menu-wrap">
                        <button class="gdpr-profile-button" data-cy="profile-button" type="button" :aria-label="accountEmail ? 'Account' : 'Inloggen'" :aria-expanded="accountEmail ? showProfileMenu : showLoginDialog" @click="openAccount">
                            <span class="gdpr-profile-button__avatar">
                                <PrototypeIcon name="user" />
                            </span>
                            <PrototypeIcon name="chevronDown" />
                        </button>
                        <div v-if="accountEmail && showProfileMenu" class="gdpr-profile-menu" data-cy="profile-menu">
                            <span>{{ accountEmail }}</span>
                            <button type="button" data-cy="logout-button" @click="handleLogout">Uitloggen</button>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <main class="gdpr-shell gdpr-workspace__main">
            <section class="gdpr-hero-card">
                <div class="gdpr-hero-card__content">
                    <h1>{{ payload.heroTitle }}</h1>
                    <button class="gdpr-button gdpr-button--primary" type="button" @click="scrollTo('gdpr-original-section')">
                        <span>{{ payload.heroButtonLabel }}</span>
                        <PrototypeIcon name="arrowDown" />
                    </button>
                </div>
            </section>

            <section class="gdpr-section-card">
                <header class="gdpr-section-card__header">
                    <p>{{ payload.workflowEyebrow }}</p>
                    <h2>{{ payload.workflowTitle }}</h2>
                </header>

                <ol class="gdpr-overview">
                    <li v-for="(step, index) in payload.steps" :key="step.key" class="gdpr-overview__item">
                        <div class="gdpr-overview__card" :class="{ 'is-active': currentStep === step.key }">
                            <div class="gdpr-overview__heading">
                                <span>{{ String(index + 1).padStart(2, '0') }}</span>
                                <PrototypeIcon :name="step.key === 'original' ? 'file' : step.key === 'anonymized' ? 'shield' : step.key === 'ai' ? 'bot' : 'userCheck'" />
                            </div>
                            <h3>{{ step.title }}</h3>
                            <p>{{ step.description }}</p>
                        </div>
                        <PrototypeIcon v-if="index < payload.steps.length - 1" class="gdpr-overview__arrow" name="arrowRight" />
                    </li>
                </ol>
            </section>

            <div v-if="requestError" class="gdpr-notice gdpr-notice--warning" data-cy="workspace-request-error">
                <div class="gdpr-notice__copy">
                    <PrototypeIcon name="alert" />
                    <p>{{ requestError }}</p>
                </div>
            </div>

            <div v-if="lastCredit" class="gdpr-notice gdpr-notice--warning" data-cy="workspace-last-credit">
                <div class="gdpr-notice__copy">
                    <PrototypeIcon name="alert" />
                    <p>{{ payload.lowCreditText }}</p>
                </div>
            </div>

            <div id="gdpr-original-section" class="gdpr-grid">
                <section class="gdpr-panel">
                    <header class="gdpr-panel__header">
                        <h2>{{ payload.originalPanel.title }}</h2>
                        <p>{{ payload.originalPanel.subtitle }}</p>
                    </header>

                    <textarea v-model="originalText"
                              class="gdpr-textarea"
                              data-cy="original-text"
                              :placeholder="payload.examplePlaceholder"></textarea>

                    <div class="gdpr-panel__actions gdpr-panel__actions--spread">
                        <button class="gdpr-button gdpr-button--secondary" type="button" @click="originalText = ''">
                            <PrototypeIcon name="eraser" />
                            <span>{{ payload.clearButtonLabel }}</span>
                        </button>
                        <button class="gdpr-button gdpr-button--primary" data-cy="anonymize-button" type="button" :disabled="!originalText.trim() || isSubmitting" @click="handleAnonymize">
                            <PrototypeIcon :name="outOfCredits ? 'lock' : 'shield'" />
                            <span>{{ payload.anonymizeButtonLabel }}</span>
                        </button>
                    </div>
                </section>

                <section class="gdpr-panel">
                    <header class="gdpr-panel__header">
                        <h2>{{ payload.anonymizedPanel.title }}</h2>
                        <p>{{ payload.anonymizedPanel.subtitle }}</p>
                    </header>

                    <template v-if="anonymizedText">
                        <textarea class="gdpr-textarea is-readonly" data-cy="anonymized-text" readonly :value="anonymizedText"></textarea>
                        <div class="gdpr-panel__summary">
                            <!--<div v-if="entitySummary.length" class="gdpr-detected">
                                <span>Gedetecteerd</span>
                                <div class="gdpr-detected__chips">
                                    <span v-for="item in entitySummary" :key="item.label" class="gdpr-chip">
                                        <PrototypeIcon name="check" />
                                        <span>{{ item.label }} · {{ item.count }}</span>
                                    </span>
                                </div>
                            </div>-->
                            <!--<span v-else class="gdpr-muted">Geen persoonsgegevens gevonden.</span>-->

                            <button class="gdpr-button gdpr-button--secondary gdpr-button--small gdpr-copy-button" :class="{ 'is-copied': copiedButton === 'anonymized' }" type="button" @click="handleCopy('anonymized', anonymizedText)">
                                <PrototypeIcon :name="copiedButton === 'anonymized' ? 'check' : 'copy'" />
                                <span>{{ copiedButton === 'anonymized' ? payload.copyConfirmationLabel : payload.copyButtonLabel }}</span>
                            </button>
                        </div>
                    </template>
                    <div v-else class="gdpr-empty-state">
                        <div class="gdpr-empty-state__icon">
                            <PrototypeIcon name="shield" />
                        </div>
                        <p>{{ payload.emptyAnonymizedState.title }}</p>
                        <small>{{ payload.emptyAnonymizedState.description }}</small>
                    </div>
                </section>
            </div>

            <section v-if="anonymizedText" class="gdpr-transition">
                <div>
                    <h3>{{ payload.transitionTitle }}</h3>
                    <p>{{ payload.transitionDescription }}</p>
                </div>
                <button class="gdpr-button gdpr-button--primary" type="button" @click="scrollTo('gdpr-ai-section')">
                    <span>{{ payload.transitionButtonLabel }}</span>
                    <PrototypeIcon name="arrowDown" />
                </button>
            </section>

            <div v-if="personalizationError" class="gdpr-notice gdpr-notice--warning" data-cy="workspace-personalization-error">
                <div class="gdpr-notice__copy">
                    <PrototypeIcon name="alert" />
                    <p>{{ personalizationError }}</p>
                </div>
            </div>

            <div id="gdpr-ai-section" class="gdpr-grid gdpr-grid--locked">
                <div v-if="!showSecondPhase" class="gdpr-lock-overlay">
                    <div class="gdpr-lock-overlay__card">
                        <div class="gdpr-lock-overlay__title">
                            <PrototypeIcon name="lock" />
                            <span>{{ payload.lockedTitle }}</span>
                        </div>
                        <p>{{ payload.lockedDescription }}</p>
                    </div>
                </div>

                <section class="gdpr-panel" :class="{ 'is-muted': !showSecondPhase }">
                    <header class="gdpr-panel__header">
                        <h2>{{ payload.aiPanel.title }}</h2>
                        <p>{{ payload.aiPanel.subtitle }}</p>
                    </header>

                    <textarea v-model="aiResponse"
                              class="gdpr-textarea"
                              data-cy="ai-response"
                              :disabled="!showSecondPhase"
                              :placeholder="payload.aiPlaceholder"></textarea>

                    <div class="gdpr-panel__actions gdpr-panel__actions--end">
                        <button class="gdpr-button gdpr-button--primary" data-cy="personalize-button" type="button" :disabled="!showSecondPhase || !aiResponse.trim() || isPersonalizing || !!personalizedText" @click="handlePersonalize">
                            <PrototypeIcon name="sparkles" />
                            <span>{{ payload.personalizeButtonLabel }}</span>
                        </button>
                    </div>
                </section>

                <section class="gdpr-panel" :class="{ 'is-muted': !showSecondPhase }">
                    <header class="gdpr-panel__header">
                        <h2>{{ payload.personalizedPanel.title }}</h2>
                        <p>{{ payload.personalizedPanel.subtitle }}</p>
                    </header>

                    <template v-if="personalizedText">
                        <textarea class="gdpr-textarea is-readonly" data-cy="personalized-text" readonly :value="personalizedText"></textarea>
                        <div class="gdpr-panel__actions gdpr-panel__actions--end">
                            <button class="gdpr-button gdpr-button--secondary gdpr-button--small gdpr-copy-button" :class="{ 'is-copied': copiedButton === 'personalized-panel' }" data-cy="personalized-copy-button" type="button" @click="handleCopyPersonalized('personalized-panel')">
                                <PrototypeIcon :name="copiedButton === 'personalized-panel' ? 'check' : 'copy'" />
                                <span>{{ copiedButton === 'personalized-panel' ? payload.copyConfirmationLabel : payload.copyButtonLabel }}</span>
                            </button>
                        </div>
                    </template>
                    <div v-else class="gdpr-empty-state">
                        <div class="gdpr-empty-state__icon">
                            <PrototypeIcon name="file" />
                        </div>
                        <p>{{ payload.emptyPersonalizedState.title }}</p>
                        <small>{{ payload.emptyPersonalizedState.description }}</small>
                    </div>
                </section>
            </div>

            <section v-if="personalizedText" class="gdpr-transition gdpr-transition--success">
                <div>
                    <h3>{{ payload.successTitle }}</h3>
                    <p>{{ payload.successDescription }}</p>
                </div>
                <div class="gdpr-transition__actions">
                    <button class="gdpr-button gdpr-button--secondary" type="button" @click="handleReset">
                        <PrototypeIcon name="rotate" />
                        <span>{{ payload.newAnonymizationLabel }}</span>
                    </button>
                    <button class="gdpr-button gdpr-button--primary gdpr-copy-button" :class="{ 'is-copied': copiedButton === 'personalized-success' }" data-cy="personalized-copy-button" type="button" @click="handleCopyPersonalized('personalized-success')">
                        <PrototypeIcon :name="copiedButton === 'personalized-success' ? 'check' : 'copy'" />
                        <span>{{ copiedButton === 'personalized-success' ? payload.copyConfirmationLabel : payload.copyButtonLabel }}</span>
                    </button>
                </div>
            </section>
        </main>

        <div v-if="showLoginDialog" class="gdpr-modal-backdrop" data-cy="login-dialog" @click.self="showLoginDialog = false">
            <section ref="loginDialog" class="gdpr-modal gdpr-login-modal" role="dialog" aria-modal="true" aria-labelledby="login-dialog-title" tabindex="-1">
                <button class="gdpr-modal__close" type="button" aria-label="Sluiten" @click="showLoginDialog = false">&times;</button>
                <h2 id="login-dialog-title">Inloggen</h2>
                <form class="gdpr-login-form" @submit.prevent="handleLogin">
                    <label>E-mailadres<input v-model="loginEmail" data-cy="login-email" type="email" autocomplete="username" required /></label>
                    <label>Wachtwoord<input v-model="loginPassword" data-cy="login-password" type="password" autocomplete="current-password" required /></label>
                    <p v-if="loginError" class="gdpr-form__error" role="alert">{{ loginError }}</p>
                    <button class="gdpr-button gdpr-button--primary" data-cy="login-submit" type="submit" :disabled="isLoggingIn">Inloggen</button>
                </form>
                <div class="gdpr-login-contact">
                    <p>{{ payload.loginContactPrompt }}</p>
                    <a class="gdpr-button gdpr-button--secondary" data-cy="login-contact-link" :href="payload.loginContactLink.url" :target="payload.loginContactLink.target || undefined" :rel="payload.loginContactLink.target === '_blank' ? 'noopener noreferrer' : undefined">{{ payload.loginContactLink.label }}</a>
                </div>
            </section>
        </div>

        <div v-if="!accountEmail && showTrialCompleteModal"
             class="gdpr-modal-backdrop"
             data-cy="trial-complete-modal"
             @click.self="closeTrialCompleteModal">
            <section ref="trialCompleteModal"
                     class="gdpr-modal"
                     role="dialog"
                     aria-modal="true"
                     aria-labelledby="trial-complete-modal-title"
                     tabindex="-1">
                <button class="gdpr-modal__close" data-cy="trial-complete-close" type="button" aria-label="Close" @click="closeTrialCompleteModal">&times;</button>
                <div class="gdpr-notice__icon">
                    <PrototypeIcon name="checkCircle" />
                </div>
                <h2 id="trial-complete-modal-title">{{ payload.exhaustedTitle }}</h2>
                <p>{{ payload.trialCompleteMessage }}</p>
                <a v-if="payload.trialCompleteLink"
                   class="gdpr-button gdpr-button--primary"
                   data-cy="trial-complete-link"
                   :href="payload.trialCompleteLink.url"
                   :target="payload.trialCompleteLink.target || undefined">
                    {{ payload.trialCompleteLink.label }}
                </a>
            </section>
        </div>
    </div>
</template>
